import React from "react";
import {
  ArrowDown,
  Building2,
  CreditCard,
  UserRound,
} from "lucide-react";

function recipientLabel(recipient) {
  return (
    recipient?.nickname ||
    recipient?.full_name ||
    "Recipient"
  );
}

function moneyLabel(currency, amount) {
  const numeric = Number(amount);

  if (!Number.isFinite(numeric)) {
    return `${currency || ""} ${amount || ""}`.trim();
  }

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${currency} ${numeric.toFixed(2)}`;
  }
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="text-xs text-[#0A2540]/45">
        {label}
      </span>

      <span className="text-right text-xs font-semibold text-[#0A2540]">
        {value}
      </span>
    </div>
  );
}

export function TransferReviewCard({
  transfer,
  quote,
}) {
  const recipient = transfer?.recipient;

  const sendCurrency =
    quote?.send_currency ||
    transfer?.sendCurrency;

  const sendAmount =
    quote?.send_amount ??
    transfer?.amount;

  const receiveCurrency =
    quote?.receive_currency ||
    recipient?.currency;

  const receiveAmount =
    quote?.receive_amount;

  const exchangeRate =
    quote?.exchange_rate;

  const fundingMethod =
    transfer?.fundingMethod;

  const FundingIcon =
    fundingMethod === "card"
      ? CreditCard
      : Building2;

  return (
    <div className="w-full max-w-[430px] overflow-hidden rounded-3xl border border-[#0A4730]/10 bg-white shadow-[0_8px_30px_rgba(10,71,48,0.07)]">
      <div className="bg-[#0A4730] px-5 py-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#C0F6B7]/70">
          Transfer
        </p>

        <div className="mt-1 flex items-center gap-2 text-white">
          <UserRound size={17} />

          <p className="font-semibold">
            {recipientLabel(recipient)}
          </p>
        </div>
      </div>

      <div className="p-5">
        <div className="rounded-2xl bg-[#F6F8F5] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#0A4730]/45">
            You send
          </p>

          <p className="mt-1 text-xl font-semibold text-[#0A4730]">
            {moneyLabel(
              sendCurrency,
              sendAmount
            )}
          </p>

          <div className="my-3 flex h-7 w-7 items-center justify-center rounded-full bg-white text-[#0A4730]/50">
            <ArrowDown size={14} />
          </div>

          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#0A4730]/45">
            They receive
          </p>

          <p className="mt-1 text-xl font-semibold text-[#0A4730]">
            {receiveAmount != null
              ? moneyLabel(
                  receiveCurrency,
                  receiveAmount
                )
              : "Calculating…"}
          </p>
        </div>

        <div className="mt-3 divide-y divide-slate-100">
          {exchangeRate && (
            <Row
              label="Rate"
              value={`1 ${sendCurrency} = ${Number(
                exchangeRate
              ).toFixed(4)} ${receiveCurrency}`}
            />
          )}

          <Row
            label="Payment"
            value={
              <span className="inline-flex items-center gap-1.5">
                <FundingIcon size={13} />
                {fundingMethod === "card"
                  ? "Card"
                  : "Bank transfer"}
              </span>
            }
          />

          {quote?.fee_amount != null && (
            <Row
              label="Fee"
              value={moneyLabel(
                sendCurrency,
                quote.fee_amount
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}